import React, { useState } from 'react';
import './JSONViewer.css';
import {useTranslation} from 'react-i18next';

/**
 * Componente interactivo para visualizar JSON y seleccionar múltiples propiedades
 * @param {Object} json - El objeto JSON a visualizar
 * @param {Array} selectedPaths - Array de rutas seleccionadas (dot notation)
 * @param {Function} onSelect - Callback cuando se selecciona/deselecciona una propiedad
 */
const JSONViewer = ({ json, selectedPaths = [], onSelect }) => {
  const {t} = useTranslation();
  const [expandedPaths, setExpandedPaths] = useState(new Set(['root']));

  // Detecta el tipo de dato real
  const getDataType = (value) => {
    if (value === null) return 'null';
    if (Array.isArray(value)) return 'array';
    return typeof value;
  };

  // Verifica si una ruta está expandida
  const isExpanded = (path) => expandedPaths.has(path);

  // Toggle expansión de objetos/arrays
  const toggleExpand = (path) => {
    const newExpanded = new Set(expandedPaths);
    if (newExpanded.has(path)) {
      newExpanded.delete(path);
    } else {
      newExpanded.add(path);
    }
    setExpandedPaths(newExpanded);
  };

  // Verifica si una ruta está seleccionada
  const isSelected = (path) => selectedPaths.includes(path);

  // Maneja el click en una propiedad (solo hojas del árbol)
  const handlePropertyClick = (path, value, e) => {
    e.stopPropagation();
    const type = getDataType(value);

    // Solo permite seleccionar valores primitivos (hojas del árbol)
    if (type === 'object' || type === 'array') {
      toggleExpand(path);
      return;
    }

    if (onSelect) {
      onSelect(path, value, type);
    }
  };

  // Renderiza un valor primitivo
  const renderValue = (value, type) => {
    switch (type) {
      case 'string':
        return <span className="json-string">"{value}"</span>;
      case 'number':
        return <span className="json-number">{value}</span>;
      case 'boolean':
        return <span className="json-boolean">{value.toString()}</span>;
      case 'null':
        return <span className="json-null">null</span>;
      default:
        return <span>{String(value)}</span>;
    }
  };

  // Renderiza un nodo del árbol JSON
  const renderNode = (obj, parentPath = '') => {
    if (obj === null || obj === undefined) {
      return renderValue(obj, 'null');
    }

    const type = getDataType(obj);

    // Si es un valor primitivo
    if (type !== 'object' && type !== 'array') {
      return renderValue(obj, type);
    }

    // Si es un objeto o array
    const entries = Array.isArray(obj)
      ? obj.map((item, idx) => [idx, item])
      : Object.entries(obj);

    const currentPath = parentPath || 'root';
    const expanded = isExpanded(currentPath);

    return (
      <div className="json-node">
        {entries.map(([key, value]) => {
          const path = parentPath ? `${parentPath}.${key}` : String(key);
          const valueType = getDataType(value);
          const isLeaf = valueType !== 'object' && valueType !== 'array';
          const selected = isSelected(path);
          const childExpanded = isExpanded(path);

          return (
            <div key={path} className="json-property">
              <div
                className={`json-property-line ${selected ? 'selected' : ''} ${isLeaf ? 'clickable' : ''}`}
                onClick={(e) => handlePropertyClick(path, value, e)}
              >
                {/* Icono de expansión para objetos/arrays */}
                {!isLeaf && (
                  <span className="expand-icon">
                    {childExpanded ? '▼' : '▶'}
                  </span>
                )}
                {isLeaf && <span className="expand-icon-placeholder"></span>}

                {/* Nombre de la propiedad */}
                <span className="json-key">{key}:</span>

                {/* Valor o preview */}
                {isLeaf ? (
                  <span className="json-value">
                    {renderValue(value, valueType)}
                  </span>
                ) : (
                  <span className="json-preview">
                    {Array.isArray(value) ? `Array[${value.length}]` : 'Object {...}'}
                  </span>
                )}
              </div>

              {/* Renderiza hijos si está expandido */}
              {!isLeaf && childExpanded && (
                <div className="json-children">
                  {renderNode(value, path)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  if (!json) {
    return (
      <div className="json-viewer-empty">
        <p>{t('adminDeviceTypes.pasteJsonToStart')}</p>
      </div>
    );
  }

  return (
    <div className="json-viewer">
      <div className="json-viewer-content">
        {renderNode(json, '')}
      </div>
    </div>
  );
};

export default JSONViewer;
